import React, { useState } from "react";
import { FaXmark, FaRegCopy } from "react-icons/fa6";
import { FaUndo } from "react-icons/fa";
import QRCode from "react-qr-code";
import Modal from "../../../Components/Modal";
import Utils from "../../../Utils/CommonProgram";

interface PinItem {
  pin: string;
  batch: string;
  /** refundData.customerNote -- what the admin wrote FOR the customer when
   *  declining. Never adminReason, which is the internal record. */
  /** The card's own id. `batch` is the serial the customer sees and quotes to
   *  support, but serials are not reliably unique -- ePay returned one SERIAL
   *  for all three cards on ORDE-304 -- so refunds travel on this instead. */
  pinId?: string;
  refundRequested: string;
  refundData: any;
}

interface OrderDetails {
  productImage: string;
  productName: string;
  quantity: number;
  totalBeforeTax: number;
  /** Face value of the card, before any discount -- what the popup shows.
   *  Same pair MyOrderPins uses for the price beside the product name. */
  originalPrice?: number;
  price?: number;
  orderNumber: string;
  orderStatus: string;
  itemNumber: string;
}

interface PinDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  pinLoading: boolean;
  pinData: PinItem[];
  orderDetails: OrderDetails;
  onRefund: (batch: string, pinId?: string) => void;
  onRefundAll: (orderNumber: string, itemNumber: string) => void; // callback for refund all
  paymentMethod: any;
}

const ViewPinsModal: React.FC<PinDetailsModalProps> = ({
  isOpen,
  onClose,
  pinLoading,
  pinData,
  orderDetails,
  onRefund,
  onRefundAll,
  paymentMethod,
}) => {
  const [isOpenConfirmation, setIsConfirmation] = useState<boolean>(false);
  const copyClipBoard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatPinStatus = (status: any) => {
    return status
      .split("_")
      .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isActionBtn={false}
        dialogClassName="p-0 w-full max-w-lg min-w-[720px] overflow-hidden align-middle transition-all transform bg-white dark:bg-dark-foreground shadow-xl rounded-2xl"
      >
        <div className="text-left px-3 relative">
          {/* Close button */}
          <button
            className="flex items-center absolute top-[-4px] right-3 gap-1 text-red-600 font-bold text-[11px] bg-red-100 rounded-full px-3 py-[3px]"
            onClick={onClose}
          >
            <FaXmark />
          </button>

          {/* Header */}
          <div className="flex items-center gap-4">
            <img
              src={orderDetails.productImage}
              alt={orderDetails.productName}
              className="w-20 h-auto rounded object-cover"
            />
            <div className="min-w-[250px] max-w-[250px]">
              <h3 className="text-lg font-semibold dark:text-white">
                {orderDetails.productName}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex flex-row items-center">
                Qty:&nbsp;
                <span className="font-medium">
                  {orderDetails.quantity > 9
                    ? orderDetails.quantity
                    : `0${orderDetails.quantity}`}
                </span>
              </p>
              {/* The denomination -- the face value of the card -- not what
                  was paid for it. This showed totalBeforeTax, the excl-VAT line
                  total, so a 10 EUR card bought at 10% off read "8,18 EUR" here
                  while the order line above said "10,00 EUR". Uses the same
                  expression as that line (MyOrderPins) so the two cannot
                  disagree. */}
              <p className="text-lg font-bold text-black dark:text-white mt-1">
                {Utils.formatEuro(
                  orderDetails?.originalPrice ||
                    orderDetails?.price ||
                    orderDetails.totalBeforeTax,
                )}
              </p>
            </div>
          </div>

          {/* Loading */}
          {pinLoading && (
            <p className="text-center text-gray-500 dark:text-gray-400 mt-4">
              Loading pin details...
            </p>
          )}

          {/* Pins header + Refund All */}
          {!pinLoading && pinData && pinData.length > 0 && (
            <>
              <hr className="my-4 border-orange-100 dark:border-dark-foreground" />
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-bold dark:text-white">
                  Pin Details
                </h4>

                {/*
                  Refund All shows only when more than one card can actually be
                  refunded.

                  A declined card used to count as eligible here, so an order
                  whose refunds had all been declined still offered the button.
                  The customer pressed it, was asked to type a reason, and only
                  then got "No refundable items found" -- the button and the
                  server disagreeing about what eligible means, with the
                  customer writing an explanation for nothing in between.

                  A decline is a decision, not a pending state: once an admin
                  has said no, that card is not claimable again.
                */}
                {Array.isArray(pinData) &&
                  pinData.filter((pin) => !pin.refundData).length > 1 &&
                  orderDetails?.orderStatus !== "refund_requested" &&
                  orderDetails?.orderStatus !== "refund_processing" && (
                    <button
                      className="flex items-center gap-2 text-white font-semibold text-sm bg-blue-600 hover:bg-blue-700 transition rounded-full px-4 py-[5px]"
                      onClick={() => {
                        onClose();
                        onRefundAll(
                          orderDetails.orderNumber,
                          orderDetails.itemNumber,
                        );
                      }}
                    >
                      <FaUndo /> Refund All
                    </button>
                  )}
              </div>
            </>
          )}

          {/* Pins list */}
          <div className="space-y-6 mt-3 px-2">
            <div className="flex flex-row flex-wrap gap-8">
              {!pinLoading &&
                pinData &&
                pinData.map((item, idx) => (
                  <div
                    key={idx}
                    className="relative flex items-center flex-row justify-center w-[45%]"
                  >
                    {/* Overlay if refund requested */}
                    {item.refundData?.refundStatus &&
                      item.refundData?.refundStatus !== "refund_declined" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[3px] rounded-md z-10">
                          <span className="text-orange-600 font-bold text-sm uppercase">
                            {formatPinStatus(item.refundData?.refundStatus)}
                          </span>
                        </div>
                      )}

                    {/* Actual content */}
                    <div
                      className={`flex items-center flex-row justify-center w-full p-2 rounded-md transition-all ${
                        item.refundData?.refundStatus &&
                        item.refundData?.refundStatus !== "refund_declined"
                          ? "blur-[2px] pointer-events-none"
                          : ""
                      }`}
                    >
                      <QRCode
                        size={256}
                        style={{
                          height: "auto",
                          maxWidth: "100%",
                          width: "80px",
                        }}
                        value={item.pin}
                        viewBox={`0 0 256 256`}
                      />
                      <div className="ml-4">
                        <p className="text-sm text-green-500 font-bold">
                          <b>PIN:&nbsp;</b>
                          {item.pin}{" "}
                          <button onClick={() => copyClipBoard(item.pin)}>
                            <FaRegCopy />
                          </button>
                        </p>
                        <p className="text-sm text-green-900 font-bold">
                          <b>B. No:&nbsp;</b>
                          {item.batch}{" "}
                          <button onClick={() => copyClipBoard(item.batch)}>
                            <FaRegCopy />
                          </button>
                        </p>
                        {item.refundData?.refundStatus !==
                          "refund_declined" ||
                        !item.refundData?.refundStatus ? (
                          <button
                            className="flex items-center gap-1 mt-1 text-white font-bold text-xs bg-blue-600 rounded-full px-3 py-[3px] hover:bg-blue-700 transition"
                            onClick={(e) => {
                              e.stopPropagation();
                              onClose();
                              onRefund(item.batch, item.pinId);
                            }}
                          >
                            <FaUndo className="text-sm" /> Refund
                          </button>
                        ) : (
                          <span className="flex flex-col gap-1 mt-1">
                            <span className="flex items-center gap-1 text-red-600 font-semibold text-sm">
                              {formatPinStatus(item.refundData?.refundStatus)}
                            </span>
                            {/*
                              Tell them WHY.
                              
                              A declined refund used to be a bare red "Refund
                              Declined" and nothing else -- the customer was
                              told no and not told why, and had no idea whether
                              it was worth contacting support.

                              This is `customerNote`, written for them in the
                              reject dialog. It is deliberately NOT adminReason:
                              that is the internal record and reads like one
                              ("rk reject", "no", "Not possible"). When no note
                              was written, neutral wording rather than silence.
                            */}
                            <span className="text-xs text-gray-600 dark:text-gray-400 leading-snug max-w-[260px]">
                              {item.refundData?.customerNote?.trim()
                                ? item.refundData.customerNote
                                : "If you would like to discuss this, please contact our support team."}
                            </span>
                          </span>
                        )}


                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isOpenConfirmation}
        onClose={() => setIsConfirmation(false)}
        title="Delete Address"
        isActionBtn={true}
        showBackButton={false}
        onConfirm={() => {
          onRefundAll(orderDetails.orderNumber, orderDetails.itemNumber);
        }}
      >
        <div className="text-md text-gray-700 dark:text-gray-300">
          <p className="text-md text-gray-700 dark:text-gray-300">
            Are you sure you want to Refund all the pins ?
          </p>
        </div>
      </Modal>
    </>
  );
};

export default ViewPinsModal;
